import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LogOut,
  Trash2,
  CheckCircle2,
  Archive,
  Mail,
  Phone,
  Building2,
  Tag,
  RefreshCw,
  Search,
  Inbox,
  Download,
} from "lucide-react";
import { apiAuth, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ASSETS } from "../data/entrepminds";

const STATUS_BADGE = {
  new: "bg-[#d4af37]/15 text-[#8a6a10] border-[#d4af37]/45",
  read: "bg-emerald-500/12 text-emerald-700 border-emerald-500/35",
  archived: "bg-[#e8e2cf] text-[#6b6356] border-[#d6cfb6]",
};

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleString("en-AE", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
};

const AdminDashboard = () => {
  const { user, status, logout } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all"); // all | new | read | archived
  const [q, setQ] = useState("");

  useEffect(() => {
    if (status === "guest") navigate("/admin/login", { replace: true });
  }, [status, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiAuth.get("/contact");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (status === "authed") load();
  }, [status]);

  const updateStatus = useCallback(async (id, newStatus) => {
    try {
      const { data } = await apiAuth.patch(`/contact/${id}`, { status: newStatus });
      setItems((arr) => arr.map((c) => (c.id === id ? data : c)));
      setSelected((sel) => (sel && sel.id === id ? data : sel));
      toast.success(`Marked as ${newStatus}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed.");
    }
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this submission? This cannot be undone.")) return;
    try {
      await apiAuth.delete(`/contact/${id}`);
      setItems((arr) => arr.filter((c) => c.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Submission deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed.");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const res = await apiAuth.get(`/contact/export${params}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `entrepminds-contacts-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const filtered = useMemo(() => {
    let arr = items;
    if (filter !== "all") arr = arr.filter((c) => (c.status || "new") === filter);
    if (q.trim()) {
      const needle = q.toLowerCase();
      arr = arr.filter(
        (c) =>
          c.name?.toLowerCase().includes(needle) ||
          c.email?.toLowerCase().includes(needle) ||
          (c.company || "").toLowerCase().includes(needle) ||
          (c.message || "").toLowerCase().includes(needle)
      );
    }
    return arr;
  }, [items, filter, q]);

  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, read: 0, archived: 0 };
    items.forEach((it) => {
      c[it.status || "new"] = (c[it.status || "new"] || 0) + 1;
    });
    return c;
  }, [items]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-[#faf7ee] text-[#6b6356] flex items-center justify-center">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf7ee] text-[#0a0a0a]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-[#faf7ee]/90 backdrop-blur-xl border-b border-[#e8e2cf]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img src={ASSETS.logo} alt="Entrepminds" className="h-10 w-auto" />
            <span className="hidden sm:block font-mono-em text-xs tracking-[0.25em] uppercase text-[#8a6a10] border-l border-[#d4af37]/45 pl-3">
              Admin
            </span>
          </a>

          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <div className="text-sm text-[#0a0a0a]">{user?.name}</div>
              <div className="text-xs text-[#6b6356]">{user?.email}</div>
            </div>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="btn-ghost text-xs"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-end justify-between flex-wrap gap-4 mb-8"
        >
          <div>
            <div className="font-mono-em text-xs tracking-[0.25em] uppercase text-[#8a6a10] mb-2">
              Inbox · Contact Submissions
            </div>
            <h1 className="font-display text-3xl md:text-4xl">Lead Pipeline</h1>
          </div>
          <button
            data-testid="admin-refresh-btn"
            onClick={load}
            className="btn-ghost text-xs"
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            data-testid="admin-export-btn"
            onClick={exportCsv}
            disabled={exporting || items.length === 0}
            className="btn-gold text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={14} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </motion.div>

        {/* Filter chips */}
        <div className="flex items-center flex-wrap gap-2 mb-6">
          {[
            { id: "all", label: "All" },
            { id: "new", label: "New" },
            { id: "read", label: "Read" },
            { id: "archived", label: "Archived" },
          ].map((f) => (
            <button
              key={f.id}
              data-testid={`admin-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-full text-xs font-medium border transition ${
                filter === f.id
                  ? "bg-[#0a0a0a] text-[#d4af37] border-[#d4af37]"
                  : "border-[#e8e2cf] text-[#3f3a30] hover:border-[#b8901f]/60"
              }`}
            >
              {f.label}
              <span className="ml-2 opacity-70">{counts[f.id] ?? 0}</span>
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 border border-[#e8e2cf] rounded-full px-4 py-2 min-w-[240px]">
            <Search size={14} className="text-[#6b6356]" />
            <input
              data-testid="admin-search-input"
              className="bg-transparent outline-none text-sm w-full text-[#0a0a0a] placeholder:text-[#9a9180]"
              placeholder="Search name, email, company…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* List */}
          <div className="lg:col-span-5 space-y-3" data-testid="admin-list">
            {loading ? (
              <div className="em-card p-8 text-center text-[#6b6356]">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="em-card p-10 text-center" data-testid="admin-empty">
                <Inbox size={28} className="text-[#8a6a10] mx-auto mb-3" />
                <div className="text-[#0a0a0a]">No submissions match this view.</div>
                <p className="text-sm text-[#6b6356] mt-2">
                  When prospects submit the contact form, they will appear here.
                </p>
              </div>
            ) : (
              filtered.map((c) => {
                const st = c.status || "new";
                const active = selected?.id === c.id;
                return (
                  <button
                    key={c.id}
                    data-testid={`admin-item-${c.id}`}
                    onClick={() => setSelected(c)}
                    className={`w-full text-left em-card p-5 transition ${
                      active ? "border-[#d4af37]/60" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[#0a0a0a] font-medium truncate">{c.name}</div>
                        <div className="text-xs text-[#6b6356] truncate">{c.email}</div>
                      </div>
                      <span
                        className={`text-[10px] uppercase font-mono tracking-wider px-2 py-1 rounded-full border ${STATUS_BADGE[st]}`}
                      >
                        {st}
                      </span>
                    </div>
                    <p className="text-sm text-[#3f3a30] mt-3 line-clamp-2">
                      {c.message}
                    </p>
                    <div className="flex items-center gap-3 mt-3 text-[11px] text-[#6b6356]">
                      {c.company && (
                        <span className="inline-flex items-center gap-1">
                          <Building2 size={11} /> {c.company}
                        </span>
                      )}
                      <span>{fmtDate(c.created_at)}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-7">
            {selected ? (
              <motion.div
                key={selected.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                data-testid="admin-detail"
                className="em-card p-7 md:p-9 sticky top-28"
              >
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div>
                    <div className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#8a6a10] mb-2">
                      {fmtDate(selected.created_at)}
                    </div>
                    <h2 className="font-display text-2xl">{selected.name}</h2>
                  </div>
                  <span
                    className={`text-[10px] uppercase font-mono tracking-wider px-2 py-1 rounded-full border ${
                      STATUS_BADGE[selected.status || "new"]
                    }`}
                  >
                    {selected.status || "new"}
                  </span>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <a
                    href={`mailto:${selected.email}`}
                    className="flex items-center gap-3 text-[#3f3a30] hover:text-[#8a6a10] transition"
                  >
                    <Mail size={16} className="text-[#8a6a10]" />
                    {selected.email}
                  </a>
                  {selected.phone && (
                    <a
                      href={`tel:${selected.phone}`}
                      className="flex items-center gap-3 text-[#3f3a30] hover:text-[#8a6a10] transition"
                    >
                      <Phone size={16} className="text-[#8a6a10]" />
                      {selected.phone}
                    </a>
                  )}
                  {selected.company && (
                    <div className="flex items-center gap-3 text-[#3f3a30]">
                      <Building2 size={16} className="text-[#8a6a10]" />
                      {selected.company}
                    </div>
                  )}
                  {selected.package && (
                    <div className="flex items-center gap-3 text-[#3f3a30]">
                      <Tag size={16} className="text-[#8a6a10]" />
                      {selected.package}
                    </div>
                  )}
                </div>

                <div className="gold-divider my-8" />

                <div className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#6b6356] mb-3">
                  Message
                </div>
                <p className="text-[#3f3a30] whitespace-pre-wrap leading-relaxed">
                  {selected.message}
                </p>

                <div className="flex flex-wrap gap-3 mt-10">
                  {selected.status !== "read" && (
                    <button
                      data-testid="admin-mark-read"
                      onClick={() => updateStatus(selected.id, "read")}
                      className="btn-ghost text-xs"
                    >
                      <CheckCircle2 size={14} /> Mark read
                    </button>
                  )}
                  {selected.status !== "archived" && (
                    <button
                      data-testid="admin-archive"
                      onClick={() => updateStatus(selected.id, "archived")}
                      className="btn-ghost text-xs"
                    >
                      <Archive size={14} /> Archive
                    </button>
                  )}
                  {selected.status !== "new" && (
                    <button
                      data-testid="admin-mark-new"
                      onClick={() => updateStatus(selected.id, "new")}
                      className="btn-ghost text-xs"
                    >
                      Reopen
                    </button>
                  )}
                  <button
                    data-testid="admin-delete"
                    onClick={() => remove(selected.id)}
                    className="text-xs inline-flex items-center gap-2 text-red-400 border border-red-400/40 hover:bg-red-400/10 px-5 py-2 rounded-full transition"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </motion.div>
            ) : (
              <div className="em-card p-10 text-center text-[#6b6356]" data-testid="admin-detail-empty">
                Select a submission to view details.
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
